import type { TransferRequestDto, TransferStateDto } from '@mimir/common/dto/transferRequestDtos';
import type { RestoreTier } from '@mimir/common/dto/restoreMediaRequestDto';
import type { CancelTokenSource } from 'axios';
import type { ArtifactReference } from '@mimir/primitive/artifactReference';
import { isProxyOnlyArtifactReference } from '@mimir/primitive/artifactReference';
import type { DeliveryStrategy, DownloadIdentity } from '../fileSync/downloadIdentity';
import type { Subclip } from './subclip';
import isEmpty from 'lodash/isEmpty';
import { invariant } from '@mimir/common/util/invariant';
import type { NleProjectBin } from '@mimir/primitive/nle/bin';
import type { MarkerInfo } from '@mimir/primitive/nle/marker';
import type { Fraction } from '@mimir/common/util/fractionType';
import type { PremiereDropFrameTimecodeDisplayFormat } from '@mimir/primitive/nle/timecode';
import type { BuiltinMediaDownloadDestination, DownloadDestination, ProjectFileDownloadDestination } from './downloadDestination';
import type { TimelineInsertion } from '@mimir/primitive/nle/sequence';

export type PercentageHandler = (percentage: number) => void;

export interface DownloadReporter {
  onSuccess(): void;
  onReject(cause: unknown): void;
  onProgress: PercentageHandler;
  onActivity(activity: string): void;
}

export type PremiereSubclip = Subclip & { premiereStartTimeSeconds: Fraction | null };

export type PostDownloadHook = (filePath: string) => Promise<void>;
export type PostDownloadHooks = PostDownloadHook[];

export type ConstructedOngoingDownload = Partial<TransferRequestDto> & {
  downloadDestination: DownloadDestination;
  artifactReference: ArtifactReference;
  importAfterCompletion?: boolean,
  isSubclipItem?: boolean;        // this is for partial downloads. not premiere subclips.
  subclip: PremiereSubclip | null;           // this is for premiere subclips.
  insertIntoSequence?: boolean;
  yetToBeSentToServer?: boolean;
  cancelTokenSource: CancelTokenSource;
  error?: any;
  restoreStatus?: OngoingRestoreStatus;
  batchImportId?: string;
  targetBin?: NleProjectBin;
  doesMasterClipExistInBin?: boolean;
  initiatedFromStorageDownloadMenu?: boolean;
  reporters?: DownloadReporter[],

  dlId: DownloadIdentity;
  deliver: DeliveryStrategy[];
  isProjectTemplate?: boolean;
  markers?: MarkerInfo[];
  sequenceOrder?: number;
  premiereStartTimeSeconds: Fraction | null;
  premiereTimeDisplayFormat: PremiereDropFrameTimecodeDisplayFormat | null;
  postDownloadHooks: PostDownloadHooks;

  mogrtChildrenMap?: Record<string, string>;

  timelineInsertion: TimelineInsertion | null;
};

/**
 * This type makes it explicit that we are dealing with a
 * download operation that is not for a project file and that
 * will be processed with the built in storage manager.
 */
export type BuiltinOngoingDownload = ConstructedOngoingDownload & { downloadDestination: BuiltinMediaDownloadDestination | ProjectFileDownloadDestination };

/*
  TODO: We have code that assumes that TransferRequestDto is assignable to
  OngoingDownload without any type-safety checks. Until this is resolved, make
  sure to not extend the OngoingDownload type with anything that would
  invalidate this assumption.
*/
export type OngoingDownload = Partial<ConstructedOngoingDownload>;

export function isConstructedOngoingDownload(dl: OngoingDownload): dl is ConstructedOngoingDownload {
  return dl.artifactReference != null && dl.cancelTokenSource != null;
}


export function isBuiltinOngoingDownload(dl: ConstructedOngoingDownload): dl is BuiltinOngoingDownload {
  return dl.downloadDestination.type === 'builtin-media-download' || dl.downloadDestination.type === 'builtin-project-file-download';
}

export type OngoingRestoreStatus = {
  tier: RestoreTier;
  retentionDays: number;
  showUpgradeOption?: boolean;
  isUpgrading?: boolean;
  progress?: number;
};

export type OngoingDownloadStatus = {
  initiated: boolean,
  percent: number,
  state?: TransferStateDto,
  restoring?: boolean,
};

export const getId = (ongoingDownload: OngoingDownload) => {
  return getOngoingDownloadId(resolveDownloadIdentity(ongoingDownload));
};

export function resolveDownloadIdentity(dl: OngoingDownload): DownloadIdentity {
  const itemId = dl.itemId;
  invariant(itemId, 'Require itemId to construct download identity');
  invariant(dl.artifactReference, 'Require artifactReference to construct download identity');
  return {
    itemId,
    artefact: dl.artefact ?? 'highres',
    artifactReference: dl.artifactReference,
  };
}

export function getOngoingDownloadId(
  ongoingDownload: DownloadIdentity,
): string {
  if (isEmpty(ongoingDownload.itemId)) {
    throw Error(`Required info missing for ongoingDownload: ${JSON.stringify(ongoingDownload, null, 2)}`);
  }

  const artRef = ongoingDownload.artifactReference;
  let suffix: string;
  if (artRef.type === 'AudioTsSegmentRange') {
    suffix = '';
  } else if (ongoingDownload.artefact === 'proxy') {
    suffix = '-proxy';
  } else if (ongoingDownload.artefact === 'aaf') {
    suffix = '-aaf';
  } else {
    suffix = '';
  }

  switch (artRef.type) {
    case 'AudioTsSegmentRange':
      suffix += '-tsr' + artRef.segmentTimes.first + ',' + artRef.segmentTimes.last;
      break;
    case 'TsSegmentRange':
      // For Flat File HighRes + TS segment proxy we always download the entire file
      // for the highres, unless the artifact reference uses index file to do partial
      // highres download
      if ((ongoingDownload.artefact === 'proxy') || (artRef.highResExt === undefined) || (artRef.useIndex)) {
        suffix += '-tsr' + artRef.segmentTimes.first + ',' + (artRef.isGrowing ? '' : artRef.segmentTimes.last);
      }
      break;
    case 'FlatFileReference':
      break;
    case 'TruncatedMp4':
      suffix += `-trmp4-${artRef.lastSegmentIndex}`;
      break;
  }

  // We don't ever want proxy-only premiere clips to relink with the highres pulled down
  // for another clip. In order to avoid this, we give a specific suffix to the highres
  // media path for proxy-only clips
  if ((ongoingDownload.artefact === 'highres') && isProxyOnlyArtifactReference(artRef)) {
    suffix += '-highres';
  }

  return `${ongoingDownload.itemId}${suffix}`;
}
